import { trackEvent } from './analytics';

/**
 * Cross-browser page-load timing, emitted once per page load, by beacon.
 *
 * Not a Core Web Vital and not a substitute for one. It covers two gaps the
 * vitals leave: CLS is Chromium-only and LCP/INP need Safari 26.2+, so some
 * patients report no vital at all; and neither vitals collector sends anything
 * for a load the patient abandons. Navigation Timing is available everywhere,
 * and a beacon outlives the document.
 *
 * Registered from App's mount effect, so it sees a load only once the bundle
 * has parsed and React has mounted. A patient who leaves before that is not
 * in this data; `complete: false` means "mounted, load event not yet fired".
 */
const EVENT = 'Page Load Timing';

/**
 * `loadEventEnd` is 0 until the load event fires, which is the normal state
 * for an abandoned load — so the event carries `complete` rather than dropping
 * those. An incomplete row is the signal, not noise.
 */
const timingProperties = (entry: PerformanceNavigationTiming) => ({
  ttfbMs: Math.round(entry.responseStart),
  responseMs: Math.round(entry.responseEnd - entry.responseStart),
  domInteractiveMs: Math.round(entry.domInteractive),
  domContentLoadedMs: Math.round(entry.domContentLoadedEventEnd),
  loadMs: entry.loadEventEnd ? Math.round(entry.loadEventEnd) : null,
  complete: entry.loadEventEnd > 0,
});

export function trackPageLoadTiming(): void {
  let sent = false;

  const send = () => {
    if (sent) return;
    const [entry] = performance.getEntriesByType(
      'navigation'
    ) as PerformanceNavigationTiming[];
    // Absent on a browser too old for Navigation Timing Level 2, and on a
    // document restored from bfcache.
    if (!entry) return;
    sent = true;
    trackEvent(EVENT, timingProperties(entry), {
      // The abandonment case is the whole point: an ordinary XHR dies with the
      // document, a beacon does not.
      send_instantly: true,
      transport: 'sendBeacon',
    });
  };

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') send();
  });
  window.addEventListener('pagehide', send);

  // A patient who stays gets reported at load rather than waiting for a hide
  // that may never come. `loadEventEnd` is only final after the event returns.
  if (document.readyState === 'complete') {
    setTimeout(send, 0);
  } else {
    window.addEventListener('load', () => setTimeout(send, 0));
  }
}
